import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import PlayerStageLockedCard from "../components/PlayerStageLockedCard";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import TableScrollWrap from "../components/TableScrollWrap";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import {
  api,
  ApiError,
  type PlayerStandingsPayload,
  type StandingsRow,
  type TournamentMatch,
} from "../lib/api";
import { scoreVocab, type ScoreVocab } from "../lib/labels";
import { playoffStageLabelsByRound } from "../lib/roundRobinPhases";
import {
  combinedRawLabelDisplay,
  matchRawLabelsBySort,
  stageForRawLabels,
} from "../lib/roundLabelAliases";
import { parseGames } from "../lib/scoreParsing";
import { playerStageLocked } from "../lib/playerStage";
import { repeatedStandingsGroupNames, standingsGroupLabel } from "../lib/groupLabels";
import { divisionStartedChecker, initialRankDisplay } from "../lib/standingsRules";

interface WeekOption {
  key: string;
  number: string;
  sort: number;
  /** Etiqueta canónica cuando la semana es una ronda de playoffs (Semifinal, Final…). */
  stage?: string;
  /** TODOS los raw labels del número (aliases "Semana 4"/"Jornada 4"), ordenados. */
  rawLabels?: string[];
}

interface RankingStats {
  w: number;
  l: number;
  gg: number;
  gp: number;
  dif: number;
}

const EMPTY_STATS: RankingStats = { w: 0, l: 0, gg: 0, gp: 0, dif: 0 };

/** Display de una semana/ronda: etapa canónica si es derivable (y cubre a
 * TODOS los aliases); si no, el/los round_label CRUDOS — un solo raw exacto o
 * el display combinado determinista "Jornada 4 · Semana 4" cuando el número
 * mezcla aliases — contrato display-only del gate #136. Compartido por el
 * <select className="t123-select"> y el resumen "Mostrando…". Exportado para regresión. */
export function weekOptionDisplay(option: { key: string; stage?: string; rawLabels?: string[] }): string {
  if (option.stage) return option.stage;
  if (option.rawLabels && option.rawLabels.length > 1) {
    return combinedRawLabelDisplay(option.rawLabels);
  }
  return option.key;
}

function roundNumber(label: string | null): string {
  if (!label) return "—";
  const found = label.match(/(\d+)/);
  return found ? found[1] : label;
}

function roundSortValue(label: string | null): number {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const found = label.match(/(\d+)/);
  return found ? Number(found[1]) : Number.MAX_SAFE_INTEGER - 1;
}

export function makeWeekOptions(matches: TournamentMatch[]): WeekOption[] {
  // Rondas de playoffs → etiqueta canónica (display-only; la clave interna
  // sigue siendo el round_label "Jornada N").
  const playoffStages = playoffStageLabelsByRound(matches);
  // TODOS los raw labels por número (aliases legados "Semana 4"/"Jornada 4"),
  // ordenados de forma determinista: la opción no depende del orden de filas.
  const rawsBySort = matchRawLabelsBySort(matches);
  const map = new Map<number, WeekOption>();
  for (const match of matches) {
    const sort = roundSortValue(match.round_label);
    if (map.has(sort)) continue;
    const raws = rawsBySort.get(sort) ?? [];
    map.set(sort, {
      key: raws[0] ?? (match.round_label || "Sin semana"),
      number: roundNumber(raws[0] ?? match.round_label),
      sort,
      stage: stageForRawLabels(raws.length > 0 ? raws : [(match.round_label ?? "").trim()], playoffStages),
      rawLabels: raws.length > 0 ? raws : undefined,
    });
  }
  return Array.from(map.values()).sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key, "es", { numeric: true }));
}

/** Selección fail-safe: acepta el key canónico o cualquiera de sus aliases
 * (defaultWeekKey devuelve round_labels de matches). Exportado para regresión. */
export function findWeekOption(options: WeekOption[], selectedKey: string): WeekOption | undefined {
  return options.find(
    (option) => option.key === selectedKey || (option.rawLabels ?? []).includes(selectedKey),
  );
}

function isTerminal(match: TournamentMatch): boolean {
  return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "cancelled", "bye"].includes(match.status);
}

function hasResult(match: TournamentMatch): boolean {
  return Boolean(match.winner_participant_id || match.score_text || ["walkover", "no_show", "defaulted", "retired", "double_walkover", "bye"].includes(match.status));
}

function defaultWeekKey(matches: TournamentMatch[], myParticipantId: number | null): string {
  const sorted = [...matches].sort((a, b) => {
    const byRound = roundSortValue(a.round_label) - roundSortValue(b.round_label);
    if (byRound !== 0) return byRound;
    return (a.match_number ?? a.id) - (b.match_number ?? b.id);
  });
  const ownCurrent = sorted.find((match) =>
    myParticipantId &&
    (match.player1_participant_id === myParticipantId || match.player2_participant_id === myParticipantId) &&
    !isTerminal(match),
  );
  if (ownCurrent?.round_label) return ownCurrent.round_label;
  const current = sorted.find((match) => !isTerminal(match));
  if (current?.round_label) return current.round_label;
  const latest = [...sorted].reverse().find((match) => match.round_label);
  return latest?.round_label || sorted[0]?.round_label || "Sin semana";
}

function addStats(stats: Map<number, RankingStats>, participantId: number | null, patch: Partial<RankingStats>) {
  if (!participantId) return;
  const current = stats.get(participantId) ?? { ...EMPTY_STATS };
  const next = {
    w: current.w + (patch.w ?? 0),
    l: current.l + (patch.l ?? 0),
    gg: current.gg + (patch.gg ?? 0),
    gp: current.gp + (patch.gp ?? 0),
    dif: 0,
  };
  next.dif = next.gg - next.gp;
  stats.set(participantId, next);
}

function latestLegacyStatsWeek(legacy: PlayerStandingsPayload["legacy_stats"] | undefined): string | null {
  if (!legacy?.available || !legacy.by_week) return null;
  const latest = Object.keys(legacy.by_week)
    .map((week) => Number(week))
    .filter((week) => Number.isFinite(week))
    .sort((a, b) => b - a)[0];
  return latest ? String(latest) : null;
}

function cumulativeStatsUntil(
  matches: TournamentMatch[],
  selectedWeekSort: number,
  sport?: string | null,
): Map<number, RankingStats> {
  const stats = new Map<number, RankingStats>();
  for (const match of matches) {
    if (roundSortValue(match.round_label) >= selectedWeekSort) continue;
    if (!hasResult(match)) continue;
    const p1 = match.player1_participant_id;
    const p2 = match.player2_participant_id;
    if (!p1 || !p2) continue;
    const sets = parseGames(match.score_text, sport);
    let p1Games = 0;
    let p2Games = 0;
    for (const [a, b] of sets) {
      p1Games += a;
      p2Games += b;
    }
    addStats(stats, p1, { gg: p1Games, gp: p2Games });
    addStats(stats, p2, { gg: p2Games, gp: p1Games });
    if (match.winner_participant_id === p1) {
      addStats(stats, p1, { w: 1 });
      addStats(stats, p2, { l: 1 });
    } else if (match.winner_participant_id === p2) {
      addStats(stats, p2, { w: 1 });
      addStats(stats, p1, { l: 1 });
    }
  }
  return stats;
}

function RankingGroupTable({
  title,
  rows,
  statsByParticipant,
  globalRankByParticipant,
  myParticipantId,
  vocab,
  started,
  divisionsWithPoints,
}: {
  title: string;
  rows: StandingsRow[];
  statsByParticipant: Map<number, RankingStats>;
  globalRankByParticipant: Map<number, number>;
  myParticipantId: number | null;
  vocab: ScoreVocab;
  /** Eduardo 2026-10-01: sin resultados todavía no hay Rk Gral. Por
   * categoría: una puede ir adelante de otra. */
  started: (divisionId: number | null | undefined) => boolean;
  /** Categorías rankeadas por puntos: ahí Rk Ini muestra los puntos (o S/R). */
  divisionsWithPoints: Set<number | null>;
}) {
  return (
    <details className={`card player-ranking-group${rows.some((row) => row.participant_id === myParticipantId) ? " is-player-group" : ""}`} open={rows.some((row) => row.participant_id === myParticipantId)}>
      <summary className="player-ranking-group__summary">
        <strong>{title}</strong>
        {rows.some((row) => row.participant_id === myParticipantId) && <span className="status-pill status-pill--active">Tu grupo</span>}
        <span aria-hidden="true">▾</span>
      </summary>
      <TableScrollWrap wrapClassName="player-ranking-table-wrap" ariaLabel={`Ranking del grupo ${title}`}>
        <table className="player-ranking-table">
          <thead>
            <tr>
              <th className="col-player">Jugador</th>
              <th title="Ranking inicial">Rk Ini</th>
              <th title="Ranking general acumulado">Rk Gral</th>
              <th title="Posición dentro del grupo">Rk Gpo</th>
              <th title="Victorias">W</th>
              <th title="Derrotas">L</th>
              <th>{vocab.ggHeader}</th>
              <th>{vocab.gpHeader}</th>
              <th>DIF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9}>Sin jugadores</td></tr>
            ) : rows.map((row, idx) => {
              const stats = statsByParticipant.get(row.participant_id) ?? EMPTY_STATS;
              const globalRank = globalRankByParticipant.get(row.participant_id);
              const isMe = row.participant_id === myParticipantId;
              return (
                <tr key={row.participant_id} className={isMe ? "player-ranking-row--me" : undefined}>
                  <td className="col-player player-ranking-player" title={row.display_name}>
                    {row.display_name}
                    {isMe && <span className="player-games-me-badge">Tú</span>}
                  </td>
                  <td>
                    {initialRankDisplay(
                      row.initial_rank ?? row.tournament_rank,
                      row.ranking_points,
                      divisionsWithPoints.has(row.division_id ?? null),
                    )}
                  </td>
                  <td>{started(row.division_id) ? globalRank ?? row.current_rank ?? "—" : "—"}</td>
                  <td>{idx + 1}</td>
                  <td>{stats.w}</td>
                  <td>{stats.l}</td>
                  <td>{stats.gg}</td>
                  <td>{stats.gp}</td>
                  <td>{stats.dif > 0 ? `+${stats.dif}` : stats.dif}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScrollWrap>
    </details>
  );
}

export default function PlayerRankingPage() {
  const { id } = useParams();
  const tournamentId = Number(id);
  const { token } = useAuth();
  const [standings, setStandings] = useState<PlayerStandingsPayload | null>(null);
  const [tournament, setTournament] = useState<{ id: number; name: string } | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [selectedWeekKey, setSelectedWeekKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cuenta familiar (Eduardo 2026-09-29): «Tú», tu grupo y tu partido son
  // los del perfil elegido para este torneo.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const [standingsResp, resultsResp] = await Promise.all([
        api.standings.forPlayer(token, tournamentId, scopeProfileId),
        api.resultReports.listTournamentResultsForPlayer(token, tournamentId, scopeProfileId),
      ]);
      if (!latestLoad.isCurrent(attempt)) return;
      setStandings(standingsResp.data);
      setTournament(resultsResp.data.tournament);
      setMatches(resultsResp.data.matches);
      setSelectedWeekKey(defaultWeekKey(resultsResp.data.matches, standingsResp.data.my_participant_id));
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el ranking.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const weekOptions = useMemo(() => makeWeekOptions(matches), [matches]);
  // Fail-safe con aliases: la clave seleccionada puede ser cualquiera de los
  // raws del bucket (defaultWeekKey devuelve labels de matches).
  const selectedWeek = findWeekOption(weekOptions, selectedWeekKey) ?? weekOptions[0];
  // El <select className="t123-select"> compara contra option.key: si la clave llegó como alias, se
  // normaliza a la clave canónica de la opción resuelta.
  const selectedWeekOptionKey = selectedWeek?.key ?? selectedWeekKey;
  const legacyStatsWeekKey = useMemo(() => latestLegacyStatsWeek(standings?.legacy_stats), [standings?.legacy_stats]);
  const statsByParticipant = useMemo(() => {
    const legacy = standings?.legacy_stats;
    if (legacy?.available) {
      // Legacy-imported ladders only expose the latest current ladder order in
      // the standings payload. Keep W/L/GG/GP on the same latest snapshot so a
      // current rank move (e.g. Marcos #1, Eduardo #2 after semana 13) does not
      // display the previous week's stats beside the new order.
      const weekStats = legacy.by_week?.[legacyStatsWeekKey ?? ""];
      if (weekStats) {
        const map = new Map<number, RankingStats>();
        for (const [pidStr, vals] of Object.entries(weekStats)) {
          const pid = Number(pidStr);
          if (!Number.isNaN(pid)) {
            map.set(pid, {
              w: vals.w ?? 0,
              l: vals.l ?? 0,
              gg: vals.gg ?? 0,
              gp: vals.gp ?? 0,
              dif: (vals.gg ?? 0) - (vals.gp ?? 0),
            });
          }
        }
        return map;
      }
    }
    return cumulativeStatsUntil(matches, selectedWeek?.sort ?? Number.MAX_SAFE_INTEGER, standings?.sport);
  }, [standings?.legacy_stats, matches, selectedWeek, legacyStatsWeekKey, standings?.sport]);

  const globalRankByParticipant = useMemo(() => {
    const map = new Map<number, number>();
    (standings?.ranking ?? []).forEach((row) => {
      map.set(row.participant_id, row.position);
    });
    return map;
  }, [standings]);

  const groups = useMemo(() => {
    return (standings?.groups ?? []).map((group) => ({
      ...group,
      standings: [...group.standings].sort((a, b) => {
        const aRank = a.current_rank ?? globalRankByParticipant.get(a.participant_id) ?? a.position;
        const bRank = b.current_rank ?? globalRankByParticipant.get(b.participant_id) ?? b.position;
        return aRank - bRank || a.display_name.localeCompare(b.display_name, "es");
      }),
    }));
  }, [globalRankByParticipant, standings]);

  // «Grupo 1» puede existir en varias categorías (Eduardo 2026-10-01).
  const repeatedGroupNames = useMemo(() => repeatedStandingsGroupNames(groups), [groups]);

  const vocab = scoreVocab(standings?.sport);
  const started = useMemo(() => divisionStartedChecker(standings), [standings]);
  const divisionsWithPoints = new Set(
    [...(standings?.ranking ?? []), ...(standings?.groups ?? []).flatMap((group) => group.standings)]
      .filter((row) => (row.ranking_points ?? 0) > 0)
      .map((row) => row.division_id ?? null),
  );

  if (Number.isNaN(tournamentId)) {
    return <Navigate to="/player/tournaments" replace />;
  }

  return (
    <>
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} />
      <PlayerProfileScopeSwitcher scope={scope} tournament />
      <section className="player-ranking-hero">
        <div>
          <span className="eyebrow">{tournament?.name ?? "Torneo"}</span>
          <h1>Ranking</h1>
          <p>Ranking por grupo semanal/post-resultados. Cada grupo representa un nivel diferente.</p>
        </div>
        <label className="player-games-round-filter player-ranking-week-filter">
          <span>Semana</span>
          <select className="t123-select"
            value={selectedWeekOptionKey}
            onChange={(event) => setSelectedWeekKey(event.target.value)}
            disabled={weekOptions.length === 0}
          >
            {weekOptions.map((option) => (
              <option key={option.key} value={option.key}>{weekOptionDisplay(option)}</option>
            ))}
          </select>
          {selectedWeek && (
            <small>
              Mostrando {weekOptionDisplay(selectedWeek)}
              {legacyStatsWeekKey && legacyStatsWeekKey !== String(selectedWeek.sort)
                ? ` · stats actuales hasta semana ${legacyStatsWeekKey}`
                : ""}
            </small>
          )}
        </label>
      </section>

      <section className="card player-ranking-legend">
        <span><strong>Rk Ini</strong> ranking inicial (puntos o seed; S/R = sin ranking)</span>
        <span><strong>Rk Gral</strong> ranking general acumulado, con los primeros resultados</span>
        <span><strong>Rk Gpo</strong> posición dentro del grupo</span>
        <span><strong>W/L</strong> victorias / derrotas</span>
        <span><strong>{vocab.ggHeader}/{vocab.gpHeader}/DIF</strong> {vocab.ggGpLegend}</span>
      </section>

      {error && <div className="alert alert--error">{error}</div>}

      {loading ? (
        <section className="card"><p className="muted">Cargando ranking…</p></section>
      ) : !standings ? (
        <section className="card"><p className="muted">Sin datos de ranking.</p></section>
      ) : playerStageLocked(standings.player_visibility) ? (
        <PlayerStageLockedCard tournamentId={tournamentId} section="ranking" />
      ) : (
        <section className="player-ranking-grid" aria-label="Ranking por grupo semanal">
          {groups.map((group) => (
            <RankingGroupTable
              key={String(group.group_id ?? "none")}
              title={standingsGroupLabel(group, repeatedGroupNames)}
              rows={group.standings}
              statsByParticipant={statsByParticipant}
              globalRankByParticipant={globalRankByParticipant}
              myParticipantId={standings.my_participant_id}
              vocab={vocab}
              started={started}
              divisionsWithPoints={divisionsWithPoints}
            />
          ))}
        </section>
      )}

      <TournamentSponsorSlots compact />
    </>
  );
}
